import { useEffect, useState } from 'react';

/** How often a clock re-reads the time; well under a second, so a seconds display never skips one. */
const DEFAULT_TICK_MS = 250;

/**
 * `Date.now()`, kept current every `tickMs` while `isActive`, for countdowns
 * and timers. Idle, it holds its last reading and costs nothing; it reads the
 * time again at once on becoming active.
 */
export function useNow(isActive: boolean, tickMs = DEFAULT_TICK_MS): number {
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        if (!isActive) {
            return undefined;
        }
        setNowMs(Date.now());
        const interval = window.setInterval(() => setNowMs(Date.now()), tickMs);
        return () => window.clearInterval(interval);
    }, [isActive, tickMs]);

    return nowMs;
}

/** Whole seconds left until `deadlineMs` (epoch ms), ticking down, never below zero; null without a deadline. */
export function useSecondsUntil(deadlineMs: number | null): number | null {
    const nowMs = useNow(deadlineMs !== null);
    return deadlineMs === null ? null : Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000));
}
